import { Badge, Banner, Button } from '@cloudflare/kumo';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { AppTabs as Tabs } from '../components/ui/AppTabs';
import { useAppToast } from '../hooks/useAppToast';
import { interactiveCampaignApi } from '../services/interactiveCampaignApi';
import { ICONS } from '../components/ui/icons';
import { SkeletonText } from '../components/ui/Skeleton';

const POLL_INTERVAL_MS = 15_000;

const cleanPhoneNumber = (phone: string): string =>
  phone ? phone.replace(/@c\.us$/, '') : phone;

function getStatusVariant(status: string): 'green' | 'yellow' | 'blue' | 'lightgray' | 'darkgray' {
  switch (status) {
    case 'STARTED': return 'green';
    case 'SCHEDULED': return 'blue';
    case 'PAUSED': return 'yellow';
    case 'COMPLETED': return 'darkgray';
    default: return 'lightgray';
  }
}

/** Cores do `Badge` do Cloudscape: `lightgray`/`darkgray` → `grey`, `yellow` → `severity-medium`. */
const BADGE_COLOR: Record<string, 'green' | 'blue' | 'red' | 'orange' | 'neutral'> = {
  green: 'green', blue: 'blue', red: 'red', yellow: 'orange', lightgray: 'neutral', darkgray: 'neutral',
};

function getStatusLabel(status: string): string {
  switch (status) {
    case 'STARTED': return 'Iniciada';
    case 'SCHEDULED': return 'Agendada';
    case 'PAUSED': return 'Pausada';
    case 'COMPLETED': return 'Finalizada';
    default: return 'Rascunho';
  }
}

export function CampanhaInterativaRelatorioPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useAppToast();

  const [reportData, setReportData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(0);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [redispatching, setRedispatching] = useState(false);
  // Motivo real de o disparo não ter contatos (conexão desconectada, categoria vazia...)
  const [problem, setProblem] = useState<string | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const silentRefresh = useCallback(async (campaignId: string) => {
    try {
      const data = await interactiveCampaignApi.getCampaignReport(campaignId);
      setReportData(data);
      setLastUpdated(new Date());
    } catch {
      // background refresh failure — don't disrupt the UI
    }
  }, []);

  useEffect(() => {
    if (id) loadReport(id);
  }, [id]);

  // Auto-poll while campaign is STARTED or PAUSED (to catch status changes from dispatch)
  useEffect(() => {
    const shouldPoll = reportData?.campaign?.status === 'STARTED' || reportData?.campaign?.status === 'PAUSED';
    if (!id || !shouldPoll) {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      return;
    }

    pollingRef.current = setInterval(() => silentRefresh(id), POLL_INTERVAL_MS);
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [id, reportData?.campaign?.status, silentRefresh]);

  const loadReport = async (campaignId: string) => {
    setLoading(true);
    try {
      const data = await interactiveCampaignApi.getCampaignReport(campaignId);
      setReportData(data);
      setLastUpdated(new Date());
    } catch (error: any) {
      toast.error(`Erro ao carregar relatório: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    if (id) loadReport(id);
  };

  const noSessions = !!reportData && (reportData.campaign?.sessions?.length ?? 0) === 0
    && ['STARTED', 'PAUSED'].includes(reportData.campaign?.status);
  useEffect(() => {
    if (!id || !noSessions) { setProblem(null); return; }
    interactiveCampaignApi.getReadiness(id).then((r) => setProblem(r.problem)).catch(() => setProblem(null));
  }, [id, noSessions, lastUpdated]);

  const handleRedispatch = async () => {
    if (!id) return;
    setRedispatching(true);
    try {
      await interactiveCampaignApi.redispatchCampaign(id);
      toast.success('Re-disparo iniciado. Aguarde alguns segundos e atualize o relatório.');
      setTimeout(() => silentRefresh(id), 6000);
    } catch (error: any) {
      toast.error(`Não foi possível re-disparar: ${error.message}`);
      setProblem(error.message);
    } finally {
      setRedispatching(false);
    }
  };

  const handleExportToExcel = () => {
    if (!reportData) return;

    try {
      const exportData = reportData.campaign.sessions.map((session: any) => {
        const row: any = {
          'Nome': session.contactName,
          'Telefone': cleanPhoneNumber(session.contactPhone),
          'Status': session.status === 'ACTIVE' ? 'Ativa' :
                    session.status === 'COMPLETED' ? 'Concluída' :
                    session.status === 'FAILED' ? 'Falhada' : 'Expirada',
        };

        if (reportData.flowNodes) {
          reportData.flowNodes.forEach((node: any) => {
            const nodeData = session.visitedNodes?.[node.id];
            row[`${node.label} (${node.type})`] = nodeData
              ? (nodeData.sent ? `OK ${new Date(nodeData.visitedAt).toLocaleString('pt-BR')}` : `Falhou ${nodeData.error || ''}`)
              : '-';
          });
        }

        return row;
      });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);

      const colWidths = [{ wch: 20 }, { wch: 15 }, { wch: 12 }];
      if (reportData.flowNodes) {
        reportData.flowNodes.forEach(() => colWidths.push({ wch: 25 }));
      }
      ws['!cols'] = colWidths;

      XLSX.utils.book_append_sheet(wb, ws, 'Relatório');

      const fileName = `relatorio_${reportData.campaign.name}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);

      toast.success('Relatório exportado com sucesso!');
    } catch (error: any) {
      toast.error(`Erro ao exportar: ${error.message}`);
    }
  };

  const stats = reportData?.stats ?? { total: 0, active: 0, completed: 0, failed: 0, expired: 0 };

  const visaoGeral = (
    <div style={{ paddingTop: 24, display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Funil de conversão */}
      {reportData.flowNodes && reportData.flowNodes.length > 0 && (
        <div>
          <div className="font-semibold" style={{ marginBottom: 12 }}>Funil de Conversão por Etapa</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {reportData.flowNodes.map((node: any, index: number) => {
              const reached = reportData.campaign.sessions.filter(
                (s: any) => s.visitedNodes?.[node.id]
              ).length;
              const sent = reportData.campaign.sessions.filter(
                (s: any) => s.visitedNodes?.[node.id]?.sent
              ).length;
              const pct = stats.total > 0 ? (reached / stats.total) * 100 : 0;

              return (
                <div key={node.id} style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%',
                    background: 'var(--brand-text)', color: 'var(--surface)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 12, fontWeight: 700, flexShrink: 0,
                  }}>
                    {index + 1}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <div style={{ fontSize: 13 }}>{node.label} <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>({node.type})</span></div>
                      <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                        {reached} alcançados · {sent} enviados · {pct.toFixed(1)}%
                      </div>
                    </div>
                    <div style={{ height: 6, background: 'var(--border)', borderRadius: 3 }}>
                      <div style={{
                        height: '100%', width: `${pct}%`,
                        background: 'var(--brand-text)', borderRadius: 3, transition: 'width 0.5s',
                      }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Taxas de conversão */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {[
          { label: 'Taxa de Conclusão', value: stats.completed, color: 'var(--brand-text)' },
          { label: 'Taxa de Erro', value: stats.failed, color: 'var(--crm-text-error)' },
        ].map(({ label, value, color }) => {
          const pct = stats.total > 0 ? (value / stats.total) * 100 : 0;
          return (
            <div key={label} style={{ border: '1px solid var(--border)', borderRadius: 6, padding: 16 }}>
              <div style={{ marginBottom: 8 }}>{label}</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{value} sessões</span>
                <span style={{ fontSize: 16, fontWeight: 700, color }}>{pct.toFixed(1)}%</span>
              </div>
              <div style={{ height: 6, background: 'var(--border)', borderRadius: 3 }}>
                <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 3 }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const porContato = (
    <div style={{ paddingTop: 24, overflowX: 'auto' }}>
      <table className="w-full">
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border)' }}>
            <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">Nome</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">Telefone</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">Status</th>
            {reportData.flowNodes?.map((node: any) => (
              <th key={node.id} className="px-4 py-3 text-center text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider min-w-[100px]">
                {node.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {reportData.campaign.sessions.map((session: any) => (
            <tr key={session.id} style={{ borderBottom: '1px solid var(--border)' }}>
              <td className="px-4 py-3 text-sm text-[var(--text)] font-medium">{session.contactName}</td>
              <td className="px-4 py-3 text-sm text-[var(--text-muted)]">{cleanPhoneNumber(session.contactPhone)}</td>
              <td className="px-4 py-3">
                <Badge variant={BADGE_COLOR[session.status === 'ACTIVE' ? 'green' : session.status === 'COMPLETED' ? 'darkgray' : session.status === 'FAILED' ? 'red' : 'yellow']}>
                  {session.status === 'ACTIVE' ? 'Ativa' :
                   session.status === 'COMPLETED' ? 'Concluída' :
                   session.status === 'FAILED' ? 'Falhada' : 'Expirada'}
                </Badge>
              </td>
              {reportData.flowNodes?.map((node: any) => {
                const nodeData = session.visitedNodes?.[node.id];
                return (
                  <td key={node.id} className="px-4 py-3 text-center text-xs">
                    {nodeData ? (
                      <span style={{
                        display: 'inline-block',
                        padding: '2px 6px',
                        borderRadius: 3,
                        background: nodeData.sent ? 'var(--tint-green)' : 'var(--tint-red)',
                        color: nodeData.sent ? 'var(--brand-text)' : 'var(--crm-text-error)',
                        fontSize: 11,
                      }}>
                        {nodeData.sent ? 'Enviado' : 'Falhou'}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--border-strong)' }}>—</span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const tabs = [
    { id: 'visao-geral', label: 'Visão Geral', content: visaoGeral },
    { id: 'por-contato', label: 'Por Contato', content: porContato },
  ];

  return (
    <div className="min-h-screen bg-[var(--surface-2)]">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '28px 40px 0', marginBottom: 24 }}>
        <div>
          <h2 className="text-xl font-semibold" style={{ marginBottom: 4, fontSize: 24, fontWeight: 600 }}>{reportData?.campaign?.name ?? 'Relatório de Campanha'}</h2>
          <div style={{ color: 'var(--text-muted)' }}>Relatório de Campanha Interativa</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {reportData?.campaign?.status === 'STARTED' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', background: 'var(--tint-green)', borderRadius: 20, border: '1px solid var(--brand-text)' }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--brand-text)', flexShrink: 0 }} />
              <div style={{ fontSize: 11, color: 'var(--brand-text)', fontWeight: 600, lineHeight: 1 }}>Ao vivo</div>
            </div>
          )}
          {lastUpdated && (
            <div style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1 }}>
              {lastUpdated.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </div>
          )}
          {reportData && (
            <Badge variant={BADGE_COLOR[getStatusVariant(reportData.campaign.status)]}>
              {getStatusLabel(reportData.campaign.status)}
            </Badge>
          )}
          <Button variant="secondary" icon={ICONS.Refresh} onClick={handleRefresh} loading={loading}>
            Atualizar
          </Button>
          <Button variant="primary" icon={ICONS.Download} onClick={handleExportToExcel} disabled={!reportData}>
            Exportar Excel
          </Button>
          <Button variant="secondary" icon={ICONS.ArrowLeft} onClick={() => navigate(-1)}>
            Voltar
          </Button>
        </div>
      </div>

      <div style={{ padding: '24px 32px' }}>
        {loading ? (
          <div className="py-8"><SkeletonText lines={4} /></div>
        ) : !reportData ? (
          <div className="text-center py-24 text-[var(--text-muted)]">
            Nenhum dado disponível para esta campanha.
          </div>
        ) : (
          <>
            {/* KPI Cards — estilo Atlas limpo, sem gradientes */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
              {[
                { label: 'Total de Contatos', value: stats.total, color: 'var(--text)' },
                { label: 'Sessões Ativas', value: stats.active, color: 'var(--brand-text)' },
                { label: 'Concluídas', value: stats.completed, color: 'var(--brand-text)' },
                { label: 'Falhadas', value: stats.failed, color: 'var(--crm-text-error)' },
              ].map((card) => (
                <div
                  key={card.label}
                  style={{
                    background: 'var(--surface)',
                    border: '1px solid var(--border)',
                    borderRadius: 6,
                    padding: '20px 24px',
                  }}
                >
                  <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>{card.label}</div>
                  <div style={{ fontSize: 32, fontWeight: 700, marginTop: 4, color: card.color }}>
                    {card.value}
                  </div>
                </div>
              ))}
            </div>

            {/* Aviso quando o disparo não gerou sessões: mostra o motivo real */}
            {(stats.total === 0 && (reportData.campaign.status === 'STARTED' || reportData.campaign.status === 'PAUSED')) && (
              <div className="mb-4">
                <Banner variant={problem ? 'error' : 'default'} description={<>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <strong>{problem ? 'O disparo não pode ser feito' : 'Disparo em andamento'}</strong>
                      <div style={{ marginTop: 2 }}>
                        {problem || 'Nenhum contato foi processado ainda. O relatório se atualiza sozinho em alguns segundos.'}
                      </div>
                    </div>
                    <Button variant="secondary" style={{ flexShrink: 0 }} loading={redispatching} onClick={handleRedispatch}>
                      Re-disparar
                    </Button>
                  </div>
                </>} />
              </div>
            )}

            {/* Abas do relatório */}
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, padding: '0 24px 24px' }}>
              <Tabs
                activeTabId={tabs[selected]?.id}
                onChange={({ detail }) => {
                  const idx = tabs.findIndex((t) => t.id === detail.activeTabId);
                  if (idx >= 0) setSelected(idx);
                }}
                tabs={tabs}
                ariaLabel="Abas do relatório"
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
